import React from 'react';
import { classNames } from 'tradera/utils/class-names';

import { ITEM_CARD_IMAGE_MODES } from './item-card-image-modes';
import { ImageLoader } from './lazy-image-loader';
import ALink from 'tradera/components/alink/alink';
import { ITEM_LAYOUT } from 'tradera/constants/ui';
import { getItemLayoutSrcSet } from './item-card-image-size';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { SecondaryImage } from './secondary-image';
import { normalizeImageUrl } from '../normalizer';

type ItemCardImageProps = {
    className?: string;
    itemUrl: string;
    itemLayout?: ITEM_LAYOUT;
    isSiteWidthFull?: boolean;
    imageHeight?: number;
    imageMode?: ITEM_CARD_IMAGE_MODES;
    imageUrl?: string;
    imageWidth?: number;
    imageSecondaryUrl?: string;
    shortDescription?: string;
    onClick?: (
        event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>
    ) => void;
    showFallback?: boolean;
    children?: React.ReactNode;
};

const normalizeItemLayout = (itemLayout: ITEM_LAYOUT, isMobile: boolean) => {
    // Normaliztion because of a long running bug where the wrong layout has been saved in a cookie
    // `layout-grid` is the default on mobile, but not a selectable option
    // `layout-grid-medium` is an option on mobile that some user's have in their cookie
    if (isMobile && itemLayout === ITEM_LAYOUT.GRID_MEDIUM) {
        return ITEM_LAYOUT.GRID;
    }

    return itemLayout;
};

export const ItemCardImage = ({
    className,
    itemUrl,
    imageHeight = 1,
    imageMode,
    imageUrl,
    imageWidth = 1,
    itemLayout = ITEM_LAYOUT.GRID,
    isSiteWidthFull = false,
    imageSecondaryUrl,
    shortDescription,
    onClick,
    showFallback = false,
    children
}: ItemCardImageProps) => {
    const isMobile = useIsMobile();

    const linkProps = {
        href: itemUrl,
        role: 'button',
        useLink: true
    };

    const imageStyle: React.CSSProperties = {};

    if (imageMode === ITEM_CARD_IMAGE_MODES.NATURAL) {
        let aspectRatio = imageHeight / imageWidth;
        aspectRatio = Math.min(aspectRatio, 3.0);
        aspectRatio = Math.max(aspectRatio, 0.33);
        imageStyle.paddingTop = aspectRatio * 100 + '%';
    }

    const Component = itemUrl ? ALink : 'div';

    const srcSetMap = getItemLayoutSrcSet({
        itemLayout: normalizeItemLayout(itemLayout, isMobile),
        isSiteWidthFull
    });

    return (
        <ImageLoader
            as={Component}
            src={normalizeImageUrl(imageUrl)}
            srcSetMap={srcSetMap}
            imageMode={imageMode}
            style={imageStyle}
            loadingClassName="animation-loading-shimmer"
            className={classNames('item-card-image', className)}
            shortDescription={shortDescription}
            showFallback={showFallback}
            onClick={onClick}
            {...(itemUrl && linkProps)}>
            <SecondaryImage
                imageSecondaryUrl={imageSecondaryUrl}
                srcSetMap={srcSetMap}
            />
            {children}
        </ImageLoader>
    );
};
